import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDownIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

function Accordion({
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn(
        'mb-2 overflow-hidden rounded-lg border border-gray-smoke bg-white shadow-sm transition-shadow duration-200 hover:shadow-md data-[state=open]:shadow-[0_8px_16px_0_rgba(0,21,90,0.15)]',
        className,
      )}
      {...props}
    />
  );
}

type AccordionTriggerProps = React.ComponentProps<
  typeof AccordionPrimitive.Trigger
> & {
  childrenClassName?: string;
};

function AccordionTrigger({
  className,
  children,
  childrenClassName,
  ...props
}: AccordionTriggerProps) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          'flex flex-1 items-center justify-between gap-3 px-5 py-4 text-left text-base font-semibold outline-none transition-all focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&>div>span]:text-dark-blue [&[data-state=open]&_*_svg]:fill-green [&[data-state=open]>div>svg]:rotate-180 [&[data-state=open]>div>svg]:text-green [&_*_svg]:fill-dark-blue [&_*_svg]:text-dark-blue',
          className,
        )}
        {...props}
      >
        <div
          className={cn(
            'flex w-full items-center justify-between gap-3 transition-colors duration-200',
            childrenClassName,
          )}
        >
          <div className="flex w-full items-center gap-3 transition-colors duration-200">
            {children}
          </div>
          <ChevronDownIcon className="h-5 w-5 !fill-none text-green transition-transform duration-200" />
        </div>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
      {...props}
    >
      <div className={cn('px-5 pb-4 text-gray-700', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
